const post = {
  slug: 'fine-tuning-vs-rag',
  number: 9,
  title: 'Fine-Tuning or RAG? You Are Probably Asking the Wrong Question',
  date: '2026-05-20',
  readTime: '7 min read',
  views: 92,
  category: 'ai',
  tags: ['AI/ML', 'RAG', 'LLM'],
  excerpt:
    'People treat fine-tuning and retrieval as a fork in the road where you pick one and live with it. They solve different problems, and most of the time the answer is not the one being argued about.',
  cover: null,
  content: `
I have sat in the meeting where someone proposes fine-tuning a model so it will "know our product." It sounds reasonable in the room. It is also, most of the time, the wrong tool aimed at the wrong problem, and it costs a team weeks before they discover that. So before any code gets written, I try to slow the conversation down to one question, because the fine-tuning-versus-retrieval debate is almost always a sign that the actual question has not been asked yet.

That question is simple. What is failing, the knowledge or the behaviour? Once you can answer that, which tool you need stops being a debate and becomes obvious.

## What each one actually changes

These two things are not competitors. They change different parts of the system.

Retrieval changes what the model knows at the moment you ask. You fetch relevant text and hand it to the model alongside the question, so the answer is grounded in information the model never saw during training. It is the tool for knowledge that changes, for facts specific to your business, and for any time you need to point at where an answer came from.

Fine-tuning changes how the model behaves. You show it many examples of the input and the output you want, and it learns the shape, the tone, the format, the way you want it to respond. It is the tool for consistency of behaviour, not for teaching new facts.

\`\`\`text
Need the model to KNOW something new   -> retrieval
Need the model to BEHAVE a certain way -> fine-tuning
\`\`\`

> Retrieval gives the model knowledge. Fine-tuning gives it behaviour. They are not substitutes for each other.

The single most common mistake I see is trying to teach facts through fine-tuning. You can do it, badly, and the moment those facts change you are retraining. That is a treadmill you do not want to be on.

## Why I reach for retrieval first

For the large majority of "we want the model to answer questions about our stuff" problems, retrieval is the whole job, and it is cheaper to start, faster to iterate, and far easier to keep current.

When a policy changes, you update a document and the next answer reflects it. There is no training run, no dataset to curate, no model to redeploy. You also get something fine-tuning cannot give you on its own: the ability to show the source. In any domain where being able to cite the answer matters, and in support, finance, and healthcare it always matters, that alone settles it.

So my default is retrieval, and I make fine-tuning earn its way in rather than assuming it.

## When fine-tuning genuinely earns its place

Fine-tuning is not a trap, it is a specialist. It becomes the right call when behaviour is the problem and the behaviour is repetitive.

- A narrow, high-volume task like classification, where you want a small, fast, consistent model instead of a large general one on every call.
- A strict output format that prompting kept drifting away from at scale.
- A task done so many times that trimming a long prompt down through fine-tuning saves real money on tokens and latency.

The common thread is repetition and consistency, not facts. If you are doing the same shaped thing millions of times and you need it identical every time, fine-tuning starts to pay for itself. If you are answering varied questions about changing information, it does not.

## The combination most teams miss

The strongest systems I have built use both, because the two tools sit at different layers. Fine-tune the model for the behaviour you want, the tone, the structure, the way it handles your domain, and use retrieval to feed it current facts at query time.

The model learns how to act once, during training, and learns what is true on every single request, through retrieval. People frame these as either-or because they are comparing them on a single axis, but they are not on the same axis at all. Behaviour and knowledge are different problems, and a mature system solves each with the tool built for it.

## A decision checklist

When the fine-tuning-versus-retrieval argument starts, I run through this:

- Is the thing failing a fact the model does not know? That is retrieval, not fine-tuning.
- Does the answer need a citation or a source? Retrieval.
- Does the information change over time? Retrieval, so you are not retraining forever.
- Is the failure a consistent format or tone the prompt cannot hold? Now fine-tuning is on the table.
- Is this a narrow task run at huge volume where cost and latency dominate? Fine-tuning may pay off.
- Do you need both correct facts and consistent behaviour? Use both, at their own layers.

## The real question

So the next time someone frames it as fine-tuning versus retrieval, do not pick a side. Ask what is actually failing, the knowledge or the behaviour. Almost every time, the honest answer is "the knowledge," and the honest answer to that is retrieval. Fine-tuning is the powerful, expensive, occasionally perfect tool you reach for when, and only when, the problem is genuinely about how the model behaves. Knowing the difference is most of the skill.
`,
};

export default post;
